'use client';
import { Address } from 'nftx.js';
import Asset from './Asset';
import useAsset from '@/application/read/assets/useAsset';
import AssetLoading from './AssetLoading';
import useVaultCollection from '@/application/read/collections/useVaultCollection';
import getWeakestStatus from '@/presentation/utils/getWeakestStatus';
import useCollection from '@/application/read/collections/useCollection';
import { useModalContext } from '@/presentation/context/modal';
import useAddress from '@/application/read/web3/useAddress';
import useUserAssets from '@/application/read/assets/useUserAssets';
import useVaultAssets from '@/application/read/assets/useVaultAssets';
import useVaults from '@/application/read/vaults/useVaults';
import { MutableRefObject } from 'react';

export interface AssetContainerProps {
  assetAddress?: Address;
  tokenId: `${number}`;
  quantity: bigint;
  collectionSlug?: string;
  root?: MutableRefObject<any>;
}

const AssetContainer = ({
  assetAddress,
  tokenId,
  quantity,
  collectionSlug,
  root,
}: AssetContainerProps) => {
  const userAddress = useAddress();
  const { showSwapModal, showCreatePositionModal } = useModalContext();
  const { data: vaultCollection, status: vaultCollectionStatus } =
    useVaultCollection(collectionSlug);

  const { data: vaults } = useVaults();
  const collectionAddress = assetAddress || vaultCollection?.collectionAddress;

  const { data: vaultAssets } = useVaultAssets({
    assetAddress: collectionAddress,
  });

  const { data: collection, status: collectionStatus } =
    useCollection(collectionAddress);

  const { data: asset, status: assetStatus } = useAsset({
    assetAddress: collectionAddress,
    tokenId,
  });

  const { data: userCollectionAssets } = useUserAssets({
    userAddress,
    assetAddress: collectionAddress,
  });

  const status = getWeakestStatus(
    assetStatus,
    collectionStatus,
    vaultCollectionStatus
  );

  if (status === 'loading') {
    return <AssetLoading />;
  }

  if (collection && asset) {
    const assetVault = vaultAssets?.find((ass) => ass.tokenId === tokenId);
    const userOwnsThisAsset =
      userCollectionAssets?.some((a) => a.tokenId === tokenId) || false;

    return (
      <Asset
        {...asset}
        root={root}
        collection={collection}
        quantity={quantity}
        vaults={vaults}
        userOwnsAsset={userOwnsThisAsset}
        vaultId={assetVault?.vaultIds[0]}
        onSwapClick={(vaultId: string) => {
          const vault = vaults?.find((v) => v.vaultId === vaultId);
          userOwnsThisAsset
            ? showSwapModal({
                sellType: 'NFT',
                sellVaultId: vaultId,
                sellVaultAddress: vault?.token.id,
                buyType: 'ETH',
                sellTokenIds: [tokenId],
              })
            : showSwapModal({
                buyTokenIds: [tokenId],
                buyType: 'NFT',
                buyVaultId: vaultId,
                buyVaultAddress: vault?.id,
              });
        }}
        onDepositClick={(vaultId: string) =>
          showCreatePositionModal({
            vaultId,
          })
        }
      />
    );
  }

  return null;
};

export default AssetContainer;
